// src/services/formsService.ts

import { Form, FormAutoSaveRequestDto, FormConfig, FormInstanceDto, GetFormInstanceDetailsRequest, RulesConfig } from "../../interfaces/dynamicForms/Forms"; // Adjust path as needed
import { PdfMappingFile } from '../../interfaces/dynamicForms/MappingTypes';
import { ApiResponse } from "../../interfaces/genericResponse/GenericResponse";

// Define the structure of the cached data
interface CachedData<T> {
    timestamp: number;
    data: T;
}

// Define the expected structure for the API responses
type FormConfigsResponse = Record<string, FormConfig>;
type FormRulesResponse = Record<string, RulesConfig>;

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL; // Your API base URL
const CACHE_TTL = 1 * 60 * 60 * 1000; // 1 hour in milliseconds (as set previously)

// --- Helper Function for Cache Handling (No changes needed) ---
function getCachedData<T>(cacheKey: string): T | null {
    const cached = localStorage.getItem(cacheKey);
    if (!cached) {
        return null;
    }

    try {
        const parsed: CachedData<T> = JSON.parse(cached);
        const now = Date.now();

        // Check if cache is expired
        if (now - parsed.timestamp > CACHE_TTL) {
            localStorage.removeItem(cacheKey); // Remove expired cache
            console.log(`Cache expired for ${cacheKey}`);
            return null;
        }
        // Cache is valid (not expired), return data
        return parsed.data;
    } catch (error) {
        console.error(`Error reading cache for ${cacheKey}:`, error);
        localStorage.removeItem(cacheKey); // Remove corrupted cache
        return null;
    }
}

function setCachedData<T>(cacheKey: string, data: T): void {
    const cacheEntry: CachedData<T> = {
        timestamp: Date.now(),
        data: data,
    };
    try {
        localStorage.setItem(cacheKey, JSON.stringify(cacheEntry));
        console.log(`Data cached for ${cacheKey}`);
    } catch (error) {
        console.error(`Error setting cache for ${cacheKey}:`, error);
        // Handle potential storage limit errors if necessary
    }
}

// --- API Fetching Functions ---

/**
 * Fetches the form configurations object from the API.
 * Caching is optional based on the useCache flag.
 * @param authToken - The user's authentication token.
 * @param useCache - If true, attempts to use cache; otherwise, fetches fresh data. Defaults to false.
 */
export async function getFormConfigs(authToken: string, useCache: boolean = false): Promise<FormConfigsResponse> {
    const cacheKey = "formConfigsCache";
    let cachedData: FormConfigsResponse | null = null;

    // 1. Check cache ONLY if useCache is true
    if (useCache) {
        cachedData = getCachedData<FormConfigsResponse>(cacheKey);
        if (cachedData) {
            console.log(`Using cached data for ${cacheKey}`);
            return cachedData; // Return valid cached data immediately
        }
        // If cache expired or doesn't exist, log and proceed to fetch
        console.log(`Cache invalid or missing for ${cacheKey}. Fetching from API.`);
    } else {
        console.log(`Cache bypassed for ${cacheKey}. Fetching from API.`);
        // Ensure any potentially stale cache is ignored if useCache is false
        // (though getCachedData already handles expiration)
    }

    // 2. Fetch from API (if cache was bypassed or invalid)
    const response = await fetch(`${API_BASE_URL}/form/configs`, { // Use correct endpoint
        method: "GET",
        headers: {
            Authorization: `Bearer ${authToken}`,
            "Content-Type": "application/json",
        },
    });

    if (!response.ok) {
        const errorText = await response.text();
        console.error("Failed to fetch form configs:", response.status, errorText);
        throw new Error(`Failed to fetch form configurations: ${response.statusText}`);
    }

    const data = await response.json();
    console.log('Fetched fresh Form Config Data', data);

    // Transform the response to match the expected interface
    const transformedData = Object.keys(data).reduce((acc, key) => {
        const config = data[key];
        const form = config.form as any; // Cast to access uppercase properties
        acc[key] = {
            ...config,
            form: {
                id: form.Id,
                name: form.Name,
            },
        };
        return acc;
    }, {} as FormConfigsResponse);

    // 3. Set cache ONLY if useCache is true
    if (useCache) {
        setCachedData(cacheKey, transformedData);
    }

    return transformedData;
}

/**
 * Fetches the form rules object from the API.
 * Caching is optional based on the useCache flag.
 * @param authToken - The user's authentication token.
 * @param useCache - If true, attempts to use cache; otherwise, fetches fresh data. Defaults to false.
 */
export async function getFormRules(authToken: string, useCache: boolean = false): Promise<FormRulesResponse> {
    const cacheKey = "formRulesCache";
    let cachedData: FormRulesResponse | null = null;

    // 1. Check cache ONLY if useCache is true
    if (useCache) {
        cachedData = getCachedData<FormRulesResponse>(cacheKey);
        if (cachedData) {
            console.log(`Using cached data for ${cacheKey}`);
            return cachedData; // Return valid cached data immediately
        }
        console.log(`Cache invalid or missing for ${cacheKey}. Fetching from API.`);
    } else {
        console.log(`Cache bypassed for ${cacheKey}. Fetching from API.`);
    }

    // 2. Fetch from API (if cache was bypassed or invalid)
    const response = await fetch(`${API_BASE_URL}/form/rules`, { // Use correct endpoint
        method: "GET",
        headers: {
            Authorization: `Bearer ${authToken}`,
            "Content-Type": "application/json",
        },
    });

    if (!response.ok) {
        const errorText = await response.text();
        console.error("Failed to fetch form rules:", response.status, errorText);
        throw new Error(`Failed to fetch form rules: ${response.statusText}`);
    }

    const data: FormRulesResponse = await response.json();
    console.log('Fetched fresh Form Rule Data', data);

    // 3. Set cache ONLY if useCache is true
    if (useCache) {
        setCachedData(cacheKey, data);
    }

    return data;
}

// Placeholder to fetch all PDF mappings
export const getPdfMappings = async (authToken: string): Promise<PdfMappingFile> => {
    // Replace with your actual API call to fetch PDF mappings
    console.log('Fetching PDF mappings...');
    // Example: return (await fetch('/api/pdf-mappings', { headers: { Authorization: `Bearer ${authToken}` } })).json();
    // For local development without a backend:
    return Promise.resolve({}); // Return empty object by default if no backend
};

// Placeholder to save a PDF mapping
export const savePdfMapping = async (mappingData: PdfMappingFile, authToken: string): Promise<void> => {
    console.log('Saving PDF mapping:', mappingData);
    // Replace with your actual API call to save PDF mappings
    // Example: await fetch('/api/pdf-mappings', { method: 'POST', headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${authToken}` }, body: JSON.stringify(mappingData) });
    return Promise.resolve(); // Simulate success
};

export const GetInstanceDetailsAsync = async (
    pdfTemplateName: string,
    participantId: string,
    taskId: number,
    token: string
): Promise<ApiResponse<FormInstanceDto>> => {
    const url = `${API_BASE_URL}/form/getFormInstanceDetails`;
    const payload: GetFormInstanceDetailsRequest = {
        pdfTemplateName: pdfTemplateName,
        participantId: participantId.toString(),
        taskId: taskId
    }
    try {
        const response = await fetch(url, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
                Authorization: `Bearer ${token}`,
            },
            body: JSON.stringify(payload),
        });

        if (!response.ok) {
            throw new Error(`HTTP error! Status: ${response.status}`);
        }

        const data = await response.json();
        return data;
    } catch (error) {
        console.error('Error fetching form:', error);
        throw error;
    }
};

export const AutoSaveFormAsync = async (
    token: string,
    payload: FormAutoSaveRequestDto
): Promise<boolean> => {
    const url = `${API_BASE_URL}/form/auto-submit-form`;
    try {
        const response = await fetch(url, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
                Authorization: `Bearer ${token}`,
            },
            body: JSON.stringify(payload),
        });

        if (!response.ok) {
            throw new Error(`HTTP error! Status: ${response.status}`);
        }

        return true;
    } catch (error) {
        console.error('Error fetching form:', error);
        throw error;
    }
};


export const GetFormReferences = async (
    token: string
): Promise<Form[]> => {
    const url = `${API_BASE_URL}/form-relationship/all`;

    try {
        const response = await fetch(url, {
            method: 'GET',
            headers: {
                'Content-Type': 'application/json',
                Authorization: `Bearer ${token}`,
            },
        });

        if (!response.ok) {
            throw new Error(`HTTP error! Status: ${response.status}`);
        }

        const data = await response.json();
        return data;
    } catch (error) {
        console.error('Error fetching form:', error);
        throw error;
    }
}